import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

dayjs.extend(customParseFormat);

const DATE_FORMAT = "MM/DD/YYYY";

// Editable date input: type, paste, or select from calendar
// value: ISO date string (YYYY-MM-DD) or null
// onChange: called with ISO date or null
// minDate, maxDate: ISO format boundaries (optional)
function EditableDatePicker({
    value,
    onChange,
    minDate,
    maxDate,
    error: externalError,
    helperText: externalHelper,
    width,
    fullWidth = false,
    size = "small",
    onValidationChange,
}) {
    const [inputText, setInputText] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [calendarOpen, setCalendarOpen] = useState(false);
    const anchorRef = useRef(null);

    // Sync display text when the external value changes
    useEffect(() => {
        if (value) {
            const d = dayjs(value);
            if (d.isValid()) {
                setInputText(d.format(DATE_FORMAT));
                return;
            }
        }
        setInputText("");
    }, [value]);

    const validateBoundaries = (d) => {
        if (minDate && d.isBefore(dayjs(minDate), "day")) {
            return `Date cannot be earlier than ${dayjs(minDate).format(DATE_FORMAT)}`;
        }
        if (maxDate && d.isAfter(dayjs(maxDate), "day")) {
            return `Date cannot be later than ${dayjs(maxDate).format(DATE_FORMAT)}`;
        }
        return "";
    };

    const handleTextChange = (e) => {
        const text = e.target.value;
        setInputText(text);

        if (text.trim() === "") {
            setErrorMessage("");
            onChange(null);
            return;
        }

        const parsed = dayjs(text, DATE_FORMAT, true);
        if (!parsed.isValid()) {
            setErrorMessage("Please enter a valid date (MM/DD/YYYY)");
            return;
        }

        const boundaryError = validateBoundaries(parsed);
        setErrorMessage(boundaryError);
        onChange(parsed.format("YYYY-MM-DD"));
    };

    const handleCalendarChange = (newValue) => {
        if (!newValue) {
            onChange(null);
            setInputText("");
            setErrorMessage("");
            return;
        }
        if (newValue.isValid()) {
            const boundaryError = validateBoundaries(newValue);
            setErrorMessage(boundaryError);
            onChange(newValue.format("YYYY-MM-DD"));
            setInputText(newValue.format(DATE_FORMAT));
        }
        setCalendarOpen(false);
    };

    // Report validation state to parent (so it can block the API call)
    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(errorMessage);
        }
    }, [errorMessage]);

    const hasError = !!errorMessage || !!externalError;
    const displayError = errorMessage || externalHelper || "";

    return (
        <>
            <TextField
                inputRef={anchorRef}
                value={inputText}
                onChange={handleTextChange}
                placeholder={DATE_FORMAT}
                fullWidth={fullWidth}
                size={size}
                variant="outlined"
                error={hasError}
                helperText={displayError}
                sx={{ backgroundColor: "white", ...(width ? { width } : {}) }}
                FormHelperTextProps={{ sx: { color: "error.main", mx: 0 } }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                edge="end"
                                size="small"
                                onClick={() => setCalendarOpen(true)}
                                aria-label="open calendar"
                            >
                                <CalendarTodayIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    open={calendarOpen}
                    onOpen={() => setCalendarOpen(true)}
                    onClose={() => setCalendarOpen(false)}
                    value={value ? dayjs(value) : null}
                    onChange={handleCalendarChange}
                    minDate={minDate ? dayjs(minDate) : undefined}
                    maxDate={maxDate ? dayjs(maxDate) : undefined}
                    slotProps={{
                        textField: { sx: { display: "none" } },
                        popper: {
                            anchorEl: anchorRef.current,
                            placement: "bottom-start",
                        },
                    }}
                />
            </LocalizationProvider>
        </>
    );
}

export default EditableDatePicker;
export { DATE_FORMAT };
